import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  cn,
} from "@racoon/ui";
import { JobEvent, ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";

import { badge } from "~/shared/helpers";

// 1. Define the toggle sequence (Carousel)
const RESOLUTION_CYCLE = [
  JobEvent.JOB_EVENT_DETACH_FROM_ROUTE,
  JobEvent.JOB_EVENT_FORCE_COMPLETE,
  JobEvent.JOB_EVENT_CANCEL,
];

// 2. Visual configuration for each resolution status
const STATUS_CONFIG: Record<string, { labelKey: string; className: string }> = {
  [JobEvent.JOB_EVENT_DETACH_FROM_ROUTE]: {
    labelKey: "detachResolution",
    className: "bg-green-100 text-green-700 hover:bg-green-200 border-green-200",
  },
  [JobEvent.JOB_EVENT_FORCE_COMPLETE]: {
    labelKey: "completeResolution",
    className: "bg-slate-500 text-white hover:bg-slate-600 border-slate-500",
  },
  [JobEvent.JOB_EVENT_CANCEL]: {
    labelKey: "cancelResolution",
    className: "bg-red-100 text-red-600 hover:bg-red-200 border-red-200",
  },
};

interface ForceCompleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unfinishedJobs: ConnectProto.Job[];
  onConfirm: (resolutions: Record<number, JobEvent>) => void;
  isLoading: boolean;
  mode?: "complete" | "cancel";
}

export const ForceCompleteModal: React.FC<ForceCompleteModalProps> = ({
  open,
  onOpenChange,
  unfinishedJobs,
  onConfirm,
  isLoading,
  mode = "complete",
}) => {
  const t = useT();

  const isCancel = mode === "cancel";
  const cycle = isCancel
    ? [JobEvent.JOB_EVENT_DETACH_FROM_ROUTE, JobEvent.JOB_EVENT_CANCEL]
    : RESOLUTION_CYCLE;

  // Store the selected resolution: { [jobId]: JobEvent }
  const [resolutions, setResolutions] = useState<Record<number, JobEvent>>({});

  // Populate with default value (DETACH) on modal open
  useEffect(() => {
    if (open && unfinishedJobs.length > 0) {
      const initial = unfinishedJobs.reduce(
        (acc, job) => {
          acc[Number(job.id)] = JobEvent.JOB_EVENT_DETACH_FROM_ROUTE;
          return acc;
        },
        {} as Record<number, JobEvent>,
      );
      setResolutions(initial);
    }
  }, [open, unfinishedJobs]);

  // Click logic (cycles to the next element in the cycle array)
  const toggleStatus = (jobId: number) => {
    setResolutions((prev) => {
      const currentEvent = prev[jobId] || JobEvent.JOB_EVENT_DETACH_FROM_ROUTE;
      const currentIndex = cycle.indexOf(currentEvent);
      const nextIndex = (currentIndex + 1) % cycle.length; // 0 -> 1 -> 0 or 0 -> 1 -> 2 -> 0

      return {
        ...prev,
        [jobId]: cycle[nextIndex],
      };
    });
  };

  const handleConfirm = () => {
    onConfirm(resolutions);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="max-w-3xl">
        <ModalHeader>
          <ModalTitle className="text-2xl font-bold">
            {isCancel
              ? t(Namespace.Routes, "forceRouteCancelTitle")
              : t(Namespace.Routes, "forceRouteCompleteTitle")}
          </ModalTitle>
          <p className="text-gray-500 mt-2">
            {isCancel
              ? t(Namespace.Routes, "forceRouteCancelDesc")
              : t(Namespace.Routes, "forceRouteCompleteDesc")}
          </p>
        </ModalHeader>

        <ModalBody className="py-6">
          <div className="grid grid-cols-[1fr_180px] gap-6 mb-3 px-1">
            <span className="font-semibold text-lg">{t(Namespace.Routes, "was")}</span>
            <span className="font-semibold text-lg">{t(Namespace.Routes, "willBe")}</span>
          </div>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto px-1 pb-2">
            {unfinishedJobs.map((job, index) => {
              const currentResolution = resolutions[Number(job.id)] || JobEvent.JOB_EVENT_DETACH_FROM_ROUTE;
              const displayConfig = STATUS_CONFIG[currentResolution];

              return (
                <div key={job.id} className="grid grid-cols-[1fr_180px] gap-6 items-center">
                  {/* LEFT SIDE: Job information */}
                  <div className="border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-white shadow-sm">
                    <div className="flex items-center gap-4">
                      {/* Number (circle) */}
                      <div className="w-8 h-8 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold text-sm shrink-0">
                        {index + 1}
                      </div>

                      {/* Info */}
                      <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold text-gray-900 text-lg">
                            {job.pickupLocation?.name || t(Namespace.Routes, "unknown")}
                          </span>
                          <span className="text-gray-400 font-mono text-sm">Z{Number(job.id)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Current status (Dynamic Badge) */}
                    {(() => {
                      const statusStr =
                        typeof job.status === "number"
                          ? `JOB_STATUS_${ConnectProto.JobStatus[job.status]}`
                          : String(job.status);
                      return badge(
                        t(Namespace.Enum, statusStr as any),
                        statusStr as any,
                      );
                    })()}
                  </div>

                  {/* RIGHT SIDE: Interactive button */}
                  <button
                    onClick={() => toggleStatus(Number(job.id))}
                    className={cn(
                      "h-full w-full rounded-xl border-2 text-sm font-bold transition-all duration-200 select-none shadow-sm active:scale-95 flex items-center justify-center",
                      displayConfig.className,
                    )}
                  >
                    {t(Namespace.Routes, displayConfig.labelKey as any)}
                  </button>
                </div>
              );
            })}
          </div>
        </ModalBody>

        <ModalFooter className="bg-gray-50 border-t border-gray-100 rounded-b-lg">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-900"
          >
            {t(Namespace.Routes, "cancel")}
          </Button>
          <Button
            className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200 px-8"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? t(Namespace.Routes, "processing")
              : isCancel
                ? t(Namespace.Routes, "forceCancel")
                : t(Namespace.Routes, "forceComplete")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
